import { Link } from "react-router-dom";
import "./legal.css";

const privacySections = [
  {
    title: "1. Who we are",
    body: (
      <>
        <p>
          Noble Academy (“we”, “us”) provides a school management website and mobile application for students, parents, teachers, and academy staff. These pages explain how personal information is collected and used when you use the Noble Academy app or web portal.
        </p>
        <p>
          The academy website is <a href="https://www.nobleacademy.org.in">www.nobleacademy.org.in</a>.
        </p>
      </>
    ),
  },
  {
    title: "2. Information we collect",
    body: (
      <>
        <p>Depending on your role, we may collect:</p>
        <ul>
          <li>Account details such as name, email address, password, role, and branch.</li>
          <li>Student profile details such as photograph, gender, date of birth, admission date, address, school, and contact numbers.</li>
          <li>Parent or guardian details such as name, occupation, and contact number.</li>
          <li>Academic records such as attendance, homework, exam marks, fees, notices, timetable, and parent-teacher meeting notes.</li>
          <li>Inquiry and admission information submitted to the academy.</li>
          <li>Files you upload, including photos, homework, notices, and study materials.</li>
          <li>A device identifier used to recognise the device that signs in to the mobile app.</li>
        </ul>
      </>
    ),
  },
  {
    title: "3. How we use information",
    body: (
      <>
        <p>We use this information to:</p>
        <ul>
          <li>Create and manage accounts for students, parents, teachers, and staff.</li>
          <li>Run day-to-day academy work such as attendance, homework, exams, fees, and notices.</li>
          <li>Show each user only the records their role is allowed to see.</li>
          <li>Store profile photos and documents needed for academic work.</li>
          <li>Keep the app secure and respond to support requests.</li>
        </ul>
        <p>We do not sell personal information and we do not use it for third-party advertising.</p>
      </>
    ),
  },
  {
    title: "4. Children and students",
    body: (
      <p>
        The app is used by a school and may include information about students who are minors. That information is collected by the academy for education and administration. Parents and academy staff can review student records through their authorised accounts. We do not use student information for marketing.
      </p>
    ),
  },
  {
    title: "5. App permissions",
    body: (
      <>
        <p>The mobile app may ask for:</p>
        <ul>
          <li>Internet access, so the app can load academy records.</li>
          <li>Camera or photo library access, when you choose a profile photo or upload homework.</li>
          <li>Storage access, so you can open or save study materials and documents.</li>
        </ul>
        <p>These permissions are used only for the feature you open. You can deny a permission in your phone settings, and the related feature will not work until it is allowed.</p>
      </>
    ),
  },
  {
    title: "6. Who can see the information",
    body: (
      <p>
        Access depends on the signed-in role. Founders, administrators, branch managers, teachers, and clerks can see the records needed for their work. Students and parents can see their own academic information. We do not make student records public.
      </p>
    ),
  },
  {
    title: "7. How long we keep information",
    body: (
      <p>
        We keep account and academic records for as long as the student or staff member is connected with the academy, and for the period the academy needs them for administration, fees, and records. When a record is removed, it may remain in backups for a limited time.
      </p>
    ),
  },
  {
    title: "8. Security",
    body: (
      <p>
        Accounts are protected with a password. The app and website talk to the academy server over the internet. No method of storage or transmission is completely secure, so please keep your password private and sign out on shared devices.
      </p>
    ),
  },
  {
    title: "9. Your choices",
    body: (
      <p>
        You may ask the academy to correct your profile details, or to review information held about a student, by contacting the academy office. Staff can update records from the admin panel. If you stop using the app, the academy may still need to keep academic and fee records.
      </p>
    ),
  },
  {
    title: "10. Changes to this policy",
    body: (
      <p>
        We may update this Privacy Policy when the app changes. The “Last updated” date at the top of this page will change when we do. Continued use of the app after an update means you accept the revised policy.
      </p>
    ),
  },
  {
    title: "11. Contact",
    body: (
      <p>
        For privacy questions, contact Noble Academy through the Contact Us section in the app, or through the academy office listed on <a href="https://www.nobleacademy.org.in">www.nobleacademy.org.in</a>.
      </p>
    ),
  },
];

const termsSections = [
  {
    title: "1. Acceptance",
    body: (
      <p>
        These Terms & Conditions apply to the Noble Academy website and mobile application. By creating an account or signing in, you agree to use the service for academy-related work and to follow these terms.
      </p>
    ),
  },
  {
    title: "2. Accounts",
    body: (
      <>
        <p>
          Accounts are issued for students, parents, teachers, clerks, branch managers, and administrators. You must:
        </p>
        <ul>
          <li>Keep your email and password confidential.</li>
          <li>Use only the account assigned to you.</li>
          <li>Tell the academy if you believe your account has been used by someone else.</li>
        </ul>
        <p>The academy may suspend an account that is misused, shared, or no longer needed.</p>
      </>
    ),
  },
  {
    title: "3. Permitted use",
    body: (
      <>
        <p>You may use the app and website to:</p>
        <ul>
          <li>View and manage attendance, homework, exams, fees, notices, and study material that your role allows.</li>
          <li>Upload photos and documents required for academy work.</li>
          <li>Update profile details that you are authorised to change.</li>
        </ul>
      </>
    ),
  },
  {
    title: "4. Prohibited use",
    body: (
      <>
        <p>You must not:</p>
        <ul>
          <li>Share another person’s login or view records you are not allowed to see.</li>
          <li>Upload unlawful, abusive, or unrelated content.</li>
          <li>Attempt to break, copy, or disrupt the app, website, or server.</li>
          <li>Use student or parent information for any purpose outside the academy.</li>
        </ul>
      </>
    ),
  },
  {
    title: "5. Student information",
    body: (
      <p>
        Staff must handle student and parent information only for academy administration. Students and parents may use the app to see their own records. Publishing or forwarding another student’s information outside the academy is not allowed.
      </p>
    ),
  },
  {
    title: "6. Content you upload",
    body: (
      <p>
        Photos, homework files, notices, and study materials you upload stay connected to the academy record they belong to. You confirm that you have the right to upload that content. The academy may remove content that is incorrect, inappropriate, or not related to academy work.
      </p>
    ),
  },
  {
    title: "7. Availability",
    body: (
      <p>
        We aim to keep the app and website available, but access may be interrupted for maintenance, network issues, or updates. Academic records shown in the app are provided for academy use and should be checked with the academy office if a result or fee entry looks incorrect.
      </p>
    ),
  },
  {
    title: "8. Privacy",
    body: (
      <p>
        Personal information is handled as described in our <Link to="/privacy-policy">Privacy Policy</Link>. Please read that page together with these terms.
      </p>
    ),
  },
  {
    title: "9. Changes",
    body: (
      <p>
        We may update these terms when the service changes. The “Last updated” date on this page will show the latest version. If you continue to sign in after an update, you accept the revised terms.
      </p>
    ),
  },
  {
    title: "10. Contact",
    body: (
      <p>
        Questions about these terms can be sent through the Contact Us section in the app, or to the academy office listed on <a href="https://www.nobleacademy.org.in">www.nobleacademy.org.in</a>.
      </p>
    ),
  },
];

function PublicLegalPage({ kind }) {
  const isPrivacy = kind === "privacy";
  const title = isPrivacy ? "Privacy Policy" : "Terms & Conditions";
  const sections = isPrivacy ? privacySections : termsSections;

  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="legal-header-inner">
          <Link to="/login" className="legal-brand">
            <img src="/assets/img/LOGO1.png" alt="Noble Academy" />
            <div>
              <strong>Noble Academy</strong>
              <span>School management</span>
            </div>
          </Link>
          <nav className="legal-nav">
            <Link to="/privacy-policy" className={isPrivacy ? "active" : ""}>
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className={!isPrivacy ? "active" : ""}>
              Terms & Conditions
            </Link>
            <Link to="/login">Login</Link>
          </nav>
        </div>
      </header>

      <section className="legal-hero">
        <div className="legal-hero-inner">
          <h1>{title}</h1>
          <p>Last updated: 29 September 2026</p>
        </div>
      </section>

      <main className="legal-body">
        <article className="legal-card">
          {sections.map((section) => (
            <section className="legal-section" key={section.title}>
              <h2>{section.title}</h2>
              {section.body}
            </section>
          ))}
        </article>
      </main>

      <footer className="legal-footer">
        <span>© {new Date().getFullYear()} Noble Academy</span>
        <span>
          <Link to="/privacy-policy">Privacy Policy</Link>
          {" · "}
          <Link to="/terms-and-conditions">Terms & Conditions</Link>
        </span>
      </footer>
    </div>
  );
}

export default PublicLegalPage;
